import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Money } from "@/components/common/Money";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, CheckCircle } from "lucide-react";

interface Payment {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  status: string;
  order: {
    publicId: string;
  };
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const params = useParams();
  const paymentId = params.paymentId as string;

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPayment();
  }, [paymentId]);

  async function fetchPayment() {
    try {
      const response = await fetch(`/api/payments/${paymentId}`);
      if (!response.ok) throw new Error("Failed to fetch payment");

      const data = await response.json();
      setPayment(data.payment);

      if (!["INITIATED", "REQUIRES_ACTION"].includes(data.payment.status)) {
        toast.error("Payment is not available for checkout");
        navigate(`/customer/orders/${data.payment.orderId}`);
      }
    } catch (error) {
      toast.error("Failed to load payment");
      navigate("/customer/orders");
    } finally {
      setLoading(false);
    }
  }

  async function handleComplete(result: "success" | "fail") {
    setProcessing(true);
    try {
      const response = await fetch(`/api/payments/${paymentId}/mock/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ result }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to complete payment");
      }

      const data = await response.json();

      if (result === "success") {
        toast.success("Payment successful!");
      } else {
        toast.error("Payment failed");
      }

      navigate(data.redirectUrl);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Payment failed");
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Checkout" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!payment) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Checkout"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(`/customer/orders/${payment.orderId}`)}
            disabled={processing}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order
          </Button>
        }
      />

      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Payment for Order {payment.order.publicId}</CardTitle>
              <Badge>Mock Payment</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-6 bg-muted rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-2">Amount to Pay</p>
              <p className="text-4xl font-bold">
                <Money amount={payment.amount} currency={payment.currency} />
              </p>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                This is a mock payment environment. Click &quot;Pay Now&quot; to simulate a successful payment, or &quot;Cancel&quot; to fail the payment.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => handleComplete("success")}
                disabled={processing}
                className="w-full"
                size="lg"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {processing ? "Processing..." : "Pay Now (Mock)"}
              </Button>

              <Button
                variant="outline"
                onClick={() => handleComplete("fail")}
                disabled={processing}
                className="w-full"
              >
                Cancel Payment
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
